import type { ReactNode } from 'react';
import { Fragment } from 'react';

import type { ApiListingLocationType } from '#/api/types/ApiListingLocationType';
import { Box } from '#/components/Box/Box';
import { Icon } from '#/components/Icon/Icon';
import { IdealistDayLabel } from '#/components/IdealistDays/IdealistDayLabel';
import { Tag } from '#/components/Tag/Tag';
import { useI18n } from '#/i18n/hooks/useI18n';
import { ListingLocationLabel } from '#/modules/listing/components/ListingLocationLabel/ListingLocationLabel';
import { ListingLocationTypeLabel } from '#/modules/listing/components/ListingLocationTypeLabel/ListingLocationTypeLabel';
import { ListingTypeLabel } from '#/modules/listing/components/ListingTypeLabel/ListingTypeLabel';
import {
  SearchHitInfoFooterHolder,
  SearchHitInfoHeader,
  SearchHitInfoHeaderHeading,
  SearchHitInfoHeaderOrgName,
  SearchHitInfoHolder,
  SearchHitInfoPipe,
  SearchHitInfoStats,
} from '#/modules/search/components/Hit/Info/SearchHitInfo.styled';
import { SearchHitApplicationStatus } from '#/modules/search/components/Hit/SearchHitApplicationStatus';
import { formatSearchHitDate } from '#/modules/search/helpers/formatSearchHitDate';
import type {
  IanaTimezone,
  DateString,
  DatetimeString,
  TimeString,
} from '#/types/TypedDateString';
import {
  safeUtf8StringSlice,
  utf8StringLength,
} from '#/utils/string/safeUtf8String';

import { SearchEventHitLabelsHolder } from './SearchEventHitInfo.styled';

type Props = {
  application: {
    status: 'ACTIVE' | 'INACTIVE';
    created: DatetimeString;
  } | null;
  startDate: DateString;
  startTime: TimeString;
  endDate: DateString | null;
  endTime: TimeString | null;
  timezone: IanaTimezone;
  location: string;
  locationType: ApiListingLocationType | null;
  listingStats: ReactNode[];
  name: string;
  orgName: string | null;
  orgType: string | null;
  showLabel: boolean;
  isIdealistDay?: boolean;
};

export function SearchEventHitInfo({
  application,
  startDate,
  startTime,
  endDate,
  endTime,
  timezone,
  location,
  locationType,
  listingStats,
  name,
  orgName,
  orgType,
  showLabel,
  isIdealistDay,
}: Props) {
  const { locale } = useI18n();

  return (
    <SearchHitInfoHolder>
      {showLabel && (
        <div style={{ marginBottom: 14 }}>
          <ListingTypeLabel listingType="EVENT" orgType={null} />
        </div>
      )}

      {isIdealistDay && (
        <Box mb="12px">
          <IdealistDayLabel />
        </Box>
      )}

      <SearchHitInfoHeader>
        <SearchHitInfoHeaderHeading>
          <div>
            <span data-qa-id="search-result-link">
              {safeUtf8StringSlice(name, 0, 120)}
            </span>
            {utf8StringLength(name) > 120 && <>&hellip;</>}
          </div>
        </SearchHitInfoHeaderHeading>

        {(orgType === 'CORPORATION' || orgName) && (
          <SearchHitInfoHeaderOrgName>
            <Box display="flex" alignItems="center" flexWrap="wrap">
              {orgName}
            </Box>
          </SearchHitInfoHeaderOrgName>
        )}
      </SearchHitInfoHeader>

      <SearchEventHitLabelsHolder>
        {locationType && (
          <ListingLocationTypeLabel
            listingType="EVENT"
            locationType={locationType}
          />
        )}

        {location && locationType && locationType !== 'REMOTE' && (
          <ListingLocationLabel
            locationType={locationType}
            location={location}
          />
        )}

        <Tag size="micro" prefix={<Icon name="calendar" size={14} />}>
          {formatSearchHitDate({
            locale,
            startDate,
            startTime,
            endDate,
            endTime,
            timezone,
          })}
        </Tag>
      </SearchEventHitLabelsHolder>

      <SearchHitInfoFooterHolder>
        {(listingStats.length > 0 || application) && (
          <SearchHitInfoStats>
            {listingStats.map((info, index) => (
              // oxlint-disable-next-line react/no-array-index-key
              <Fragment key={index}>
                {index > 0 && <SearchHitInfoPipe />}
                {info}
              </Fragment>
            ))}
          </SearchHitInfoStats>
        )}

        {application && (
          <SearchHitApplicationStatus
            status={application.status}
            created={application.created}
          />
        )}
      </SearchHitInfoFooterHolder>
    </SearchHitInfoHolder>
  );
}
